import {
  CellEditingStoppedEvent,
  CellPosition,
  GridApi,
  IRowNode,
  SuppressKeyboardEventParams,
  TabToNextCellParams,
} from 'ag-grid-community';
import { atom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import {
  CompanySearchResponse,
  createCompanySearchResponse,
} from '../../../schemas/CompanySearchResponse.schema';
import { InvestmentType } from '../../../schemas/FundReserves.schema';
import { portcosByNameMapAtomJ } from '../../../services/state/PortCosStateJ';
import { companyToSearchResult } from '../../../util/companyUtils';
import { selectedFundInvestmentsByCompanyNameState, selectedFundReservesState } from '../state/FPState';
import { getInvestmentSchema } from './addInvestmentFormUtils';
import { useReservesActions } from './reservesActions';
import {
  createReservesDraftRow,
  isReservesDraftRow,
  ReservesDraftRow,
  ReservesRowData,
} from './reservesGridData';

export const reservesDraftRowState = atom<ReservesDraftRow | null>(null);
const reservesDraftGridApiState = atom<GridApi | null>(null);

export const draftAmountColId = 'investmentAmount';
export const draftDateColId = 'date';

type DraftEditIntent = 'commit' | 'cancel';

function isDraftEditableColId(colId?: string | null) {
  return colId === draftAmountColId || colId === draftDateColId;
}

function buildDraftCompany(
  portcosByName: Map<string, ICompanyDataModel>,
  companyName: string
): CompanySearchResponse {
  const portco = portcosByName.get(companyName);
  if (portco) {
    return companyToSearchResult(portco) as CompanySearchResponse;
  }
  return createCompanySearchResponse({ fields: { name: companyName } } as CompanySearchResponse);
}

function restartDraftEditing(api: GridApi, draft: ReservesDraftRow, colKey: string) {
  // startEditingCell silently no-ops on hidden columns
  api.setColumnsVisible([draftAmountColId, draftDateColId], true);
  const node = api.getRowNode(draft.uuid ?? '');
  if (node?.rowIndex != null) {
    api.startEditingCell({ rowIndex: node.rowIndex, colKey });
  }
}

function getFirstInvalidDraftColId(draft: ReservesDraftRow) {
  const amountValid = typeof draft.investmentAmount === 'number' && Number.isFinite(draft.investmentAmount);
  if (!amountValid) {
    return draftAmountColId;
  }
  const date = draft.date;
  // empty is allowed (undefined on a fresh draft, null from a cleared editor); a half-typed Invalid Date is not
  const dateValid = date == null || (date instanceof Date && !Number.isNaN(date.getTime()));
  if (!dateValid) {
    return draftDateColId;
  }
  return null;
}

export function useStartDraftInvestment() {
  return useAtomCallback(
    useCallback(async (get, set, node: IRowNode<ReservesRowData>, api: GridApi) => {
      if (!node.group || node.level !== 0) {
        return;
      }
      const leaves = node.allLeafChildren ?? [];
      // groups are keyed by name, so a same-named hypothetical can precede the real portco
      const sourceRow = (
        leaves.find((leaf) => leaf.data?.companyId != null) ?? leaves.find((leaf) => leaf.data)
      )?.data;
      if (!sourceRow) {
        return;
      }
      api.stopEditing(true);
      const nameToInvestment = await get(selectedFundInvestmentsByCompanyNameState);
      const portcosByName = await get(portcosByNameMapAtomJ);
      const companyName = node.key ?? sourceRow.company?.name ?? '';
      const company = buildDraftCompany(portcosByName, companyName);
      const { type } = getInvestmentSchema({ company, nameToInvestment }).getDefault();
      const draft = createReservesDraftRow({ sourceRow, type: type as InvestmentType });
      set(reservesDraftRowState, draft);
      set(reservesDraftGridApiState, api);

      const tryFocusDraft = (attempt: number) => {
        if (api.isDestroyed()) {
          return;
        }
        const draftNode = api.getRowNode(draft.uuid ?? '');
        if (draftNode) {
          // getRowId keeps stale expansion state across rowData swaps, so expand imperatively
          let parent = draftNode.parent;
          while (parent && parent.level >= 0) {
            parent.setExpanded(true);
            parent = parent.parent;
          }
          api.setColumnsVisible([draftAmountColId, draftDateColId], true);
        }
        if (draftNode?.rowIndex != null) {
          api.ensureNodeVisible(draftNode, 'middle');
          api.startEditingCell({ rowIndex: draftNode.rowIndex, colKey: draftAmountColId });
          return;
        }
        // the node and its display index can lag the rowData swap and expansion by a few frames
        if (attempt < 10) {
          requestAnimationFrame(() => tryFocusDraft(attempt + 1));
        }
      };
      const focusDraft = () => {
        api.removeEventListener('rowDataUpdated', focusDraft);
        requestAnimationFrame(() => tryFocusDraft(0));
      };
      api.addEventListener('rowDataUpdated', focusDraft);
    }, [])
  );
}

export function useCommitDraftInvestment() {
  const { addInvestment } = useReservesActions();
  const { pushErrorToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (get, set, api: GridApi) => {
        const draft = get(reservesDraftRowState);
        if (!draft) {
          return;
        }
        const invalidColId = getFirstInvalidDraftColId(draft);
        if (invalidColId) {
          pushErrorToast({
            message:
              invalidColId === draftDateColId
                ? 'Enter a valid date or clear it to save the investment'
                : 'Enter an amount to save the investment',
          });
          restartDraftEditing(api, draft, invalidColId);
          return;
        }
        const reserves = await get(selectedFundReservesState);
        const nameToInvestment = await get(selectedFundInvestmentsByCompanyNameState);
        const portcosByName = await get(portcosByNameMapAtomJ);
        const companyName = draft.company?.name ?? draft.companyName ?? '';
        // resolve by the draft's own ids - a same-named hypothetical can clobber the name map
        const investments = reserves?.investments ?? [];
        const investment =
          investments.find((inv) => inv.id != null && inv.id === draft.investmentId) ??
          (draft.companyId != null
            ? investments.find((inv) => inv.companyId === draft.companyId)
            : undefined) ??
          nameToInvestment.get(companyName);
        const result = await addInvestment({
          company: buildDraftCompany(portcosByName, companyName),
          description: investment?.description ?? '',
          investment,
          sectorId: investment?.sectorId ?? null,
          transaction: {
            amount: draft.investmentAmount,
            date: draft.date ?? null,
            hypothetical: true,
            type: draft.type,
          },
        });
        // a replacement draft may have been started while the request was in flight
        const stillCurrent = get(reservesDraftRowState)?.uuid === draft.uuid;
        if (!stillCurrent) {
          return;
        }
        if (result) {
          set(reservesDraftRowState, null);
        } else {
          restartDraftEditing(api, draft, draftAmountColId);
        }
      },
      [addInvestment, pushErrorToast]
    )
  );
}

export function useDiscardDraftInvestment() {
  return useAtomCallback(
    useCallback((get, set) => {
      set(reservesDraftRowState, null);
    }, [])
  );
}

export function useReservesDraftGridCallbacks(containerRef: RefObject<HTMLDivElement | null>) {
  const intentRef = useRef<{ uuid: string; intent: DraftEditIntent } | null>(null);
  const inFlightUuidsRef = useRef(new Set<string>());
  const commitDraft = useCommitDraftInvestment();

  const resolveEditingStopped = useAtomCallback(
    useCallback(
      async (get, set, api: GridApi, stoppedColId: string, stoppedUuid: string) => {
        // only consume an intent recorded for this row - a replaced draft must not steal it
        const intent = intentRef.current?.uuid === stoppedUuid ? intentRef.current.intent : null;
        if (intent) {
          intentRef.current = null;
        }
        if (inFlightUuidsRef.current.has(stoppedUuid) || api.isDestroyed()) {
          return;
        }
        const draft = get(reservesDraftRowState);
        // a newer draft owns the grid now - ignore the old row's trailing stop event
        if (!draft || draft.uuid !== stoppedUuid) {
          return;
        }
        if (intent === 'cancel') {
          set(reservesDraftRowState, null);
          return;
        }
        if (intent === 'commit') {
          inFlightUuidsRef.current.add(stoppedUuid);
          try {
            await commitDraft(api);
          } finally {
            inFlightUuidsRef.current.delete(stoppedUuid);
          }
          return;
        }
        if (api.getEditingCells().length > 0) {
          return;
        }
        const focused = api.getFocusedCell();
        if (focused) {
          const focusedColId = focused.column.getColId();
          const focusedRow = api.getDisplayedRowAtIndex(focused.rowIndex);
          const movedToOtherDraftCell =
            isReservesDraftRow(focusedRow?.data) &&
            isDraftEditableColId(focusedColId) &&
            focusedColId !== stoppedColId;
          if (movedToOtherDraftCell) {
            api.startEditingCell({ rowIndex: focused.rowIndex, colKey: focusedColId });
            return;
          }
        }
        set(reservesDraftRowState, null);
      },
      [commitDraft]
    )
  );

  const onCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent<ReservesRowData>) => {
      if (!isReservesDraftRow(event.data)) {
        return;
      }
      const stoppedColId = event.column?.getColId() ?? '';
      const stoppedUuid = event.data.uuid ?? '';
      // defer so tabbing / restarted editing is observable before deciding to discard
      window.setTimeout(() => {
        void resolveEditingStopped(event.api, stoppedColId, stoppedUuid);
      }, 0);
    },
    [resolveEditingStopped]
  );

  const suppressKeyboardEvent = useCallback((params: SuppressKeyboardEventParams<ReservesRowData>) => {
    if (!params.editing || !isReservesDraftRow(params.data)) {
      return false;
    }
    const uuid = params.data.uuid ?? '';
    if (params.event.key === 'Enter') {
      intentRef.current = { uuid, intent: 'commit' };
    } else if (params.event.key === 'Escape') {
      intentRef.current = { uuid, intent: 'cancel' };
    } else if (params.event.key === 'Tab') {
      // the grid's own tab handling does not fire from inside these editors, so move explicitly
      const rowIndex = params.node?.rowIndex;
      const nextColId = params.column.getColId() === draftAmountColId ? draftDateColId : draftAmountColId;
      params.event.preventDefault();
      if (rowIndex != null) {
        window.setTimeout(() => {
          if (!params.api.isDestroyed()) {
            params.api.startEditingCell({ rowIndex, colKey: nextColId });
          }
        }, 0);
      }
      return true;
    }
    return false;
  }, []);

  const tabToNextCell = useCallback(
    (params: TabToNextCellParams<ReservesRowData>): CellPosition | boolean => {
      const { rowIndex, column, rowPinned } = params.previousCellPosition;
      const rowNode = params.api.getDisplayedRowAtIndex(rowIndex);
      if (!isReservesDraftRow(rowNode?.data)) {
        return params.nextCellPosition ?? false;
      }
      const nextColId = column.getColId() === draftAmountColId ? draftDateColId : draftAmountColId;
      const nextColumn = params.api.getColumn(nextColId);
      if (!nextColumn) {
        return params.nextCellPosition ?? false;
      }
      return { rowIndex, column: nextColumn, rowPinned };
    },
    []
  );

  const discardOnOutsideMouseDown = useAtomCallback(
    useCallback(
      (get, set, event: MouseEvent) => {
        const target = event.target;
        if (!(target instanceof Element)) {
          return;
        }
        if (
          containerRef.current?.contains(target) ||
          target.closest('.MuiPickersPopper-root, .MuiSnackbar-root')
        ) {
          return;
        }
        const api = get(reservesDraftGridApiState);
        if (api && !api.isDestroyed()) {
          api.stopEditing(true);
        }
        set(reservesDraftRowState, null);
      },
      [containerRef]
    )
  );

  const hasDraft = useAtomCallback(useCallback((get) => get(reservesDraftRowState) != null, []));

  useEffect(() => {
    // the grid-managed date editor ignores blur, so outside clicks need an explicit discard
    const onMouseDown = (event: MouseEvent) => {
      if (hasDraft()) {
        discardOnOutsideMouseDown(event);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [discardOnOutsideMouseDown, hasDraft]);

  const resetDraftState = useAtomCallback(
    useCallback((get, set) => {
      set(reservesDraftRowState, null);
      set(reservesDraftGridApiState, null);
    }, [])
  );

  useEffect(() => {
    return () => resetDraftState();
  }, [resetDraftState]);

  return { onCellEditingStopped, suppressKeyboardEvent, tabToNextCell };
}
